"use client"

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

type Theme = "light" | "dark"
type ThemeContextValue = {
  resolvedTheme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [resolvedTheme, setResolvedTheme] = useState<Theme>("light")

  useEffect(() => {
    setResolvedTheme(document.documentElement.dataset.theme === "dark" ? "dark" : "light")
  }, [])

  const setTheme = useCallback((theme: Theme) => {
    document.documentElement.dataset.theme = theme
    document.documentElement.classList.toggle("dark", theme === "dark")
    localStorage.setItem("resolto-theme", theme)
    setResolvedTheme(theme)
  }, [])

  const value = useMemo(() => ({ resolvedTheme, setTheme }), [resolvedTheme, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useResoltoTheme(): ThemeContextValue {
  const value = useContext(ThemeContext)
  if (!value) throw new Error("Theme controls must be rendered inside ThemeProvider")
  return value
}
